import { useState } from 'react';
import { authApi } from '../api/auth';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { SelectField, TextField } from '../ui/Field';

const TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

/**
 * Step two of founding a unit: the invitee is signed in, and now names the unit.
 *
 * Separate from sign-in so an abandoned form does not consume the invite — the
 * API only marks it claimed when a unit is actually created.
 */
export function FoundUnitScreen({
  inviteId,
  onCreated,
}: {
  inviteId: string;
  onCreated: (unitId: string) => void;
}) {
  const [unitType, setUnitType] = useState('Troop');
  const [unitNumber, setUnitNumber] = useState('');
  const [name, setName] = useState('');
  const [timeZoneId, setTimeZoneId] = useState(
    // Their own zone is right far more often than not, so it is the default rather
    // than an empty select.
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const suggestedName = unitNumber ? `${unitType} ${unitNumber}` : '';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await authApi.foundUnit({
        inviteId,
        unitType,
        unitNumber: unitNumber.trim(),
        name: (name || suggestedName).trim(),
        timeZoneId,
      });
      onCreated(result.unitId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the unit.');
      setBusy(false);
    }
  }

  return (
    <Page>
      <Gutter>
        <div style={{ paddingBlock: 'var(--space-7)', maxWidth: '32rem' }}>
          <p className="t-eyebrow" style={{ color: 'var(--color-accent-announcement-text)', margin: 0 }}>
            Step 2 of 2
          </p>

          <h1
            className="t-heading"
            style={{
              color: 'var(--color-text-primary)',
              margin: 'var(--space-2) 0 var(--space-3)',
              textWrap: 'balance',
            }}
          >
            Name your unit
          </h1>

          <p
            className="t-body"
            style={{ color: 'var(--color-text-secondary)', margin: '0 0 var(--space-6)' }}
          >
            You&rsquo;ll be this unit&rsquo;s first admin. You can add everyone else once it exists.
          </p>

          <form onSubmit={submit} className="flex flex-col" style={{ gap: 'var(--space-5)' }}>
            <SelectField
              label="Type"
              required
              value={unitType}
              onChange={(e) => setUnitType(e.target.value)}
            >
              <option value="Troop">Troop</option>
              <option value="Pack">Pack</option>
            </SelectField>

            <TextField
              label="Number"
              required
              value={unitNumber}
              onChange={(e) => setUnitNumber(e.target.value)}
              placeholder="119"
              inputMode="numeric"
              autoComplete="off"
            />

            <TextField
              label="Display name"
              required={false}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={suggestedName || 'Troop 119'}
              hint="Leave blank to use the type and number."
              autoComplete="off"
            />

            <SelectField
              label="Time zone"
              required
              value={timeZoneId}
              onChange={(e) => setTimeZoneId(e.target.value)}
              // Not cosmetic: departure times are stored as instants, so the unit's
              // zone is what turns one back into "5pm Friday" for every family.
              hint="Used to show departure and return times correctly."
            >
              {(TIME_ZONES.includes(timeZoneId) ? TIME_ZONES : [timeZoneId, ...TIME_ZONES]).map(
                (zone) => (
                  <option key={zone} value={zone}>
                    {zone.replace('_', ' ')}
                  </option>
                ),
              )}
            </SelectField>

            {error && (
              <p
                className="t-body"
                role="alert"
                style={{
                  background: 'var(--color-status-blocked-surface)',
                  border: '1px solid var(--color-status-blocked)',
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--space-3)',
                  color: 'var(--color-text-primary)',
                  margin: 0,
                }}
              >
                {error}
              </p>
            )}

            <div>
              <Button
                variant="primary"
                type="submit"
                disabledReason={!unitNumber.trim() ? 'Enter a unit number to continue.' : undefined}
              >
                {busy ? 'Creating…' : 'Create unit'}
              </Button>
            </div>
          </form>
        </div>
      </Gutter>
      <Footer />
    </Page>
  );
}
